import { cx } from '@linaria/core';
import { useId, useRef, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { resets } from '@ui/css';
import { resolveComputedCssValues, verifyWCAGContrastRatio } from '@utils/css';
import { useToggleState, useIsomorphicLayoutEffect, SizeTransition } from '@utils/react';
import { colors } from '../../../design-tokens/colors.js';
import { surfaces } from '../../../design-tokens/surfaces.js';
import { truncateWrapper, truncateTransitionDurationMs, transition, expandButton } from './_input-toggle-group.style.js';
import { list, legend, noMatches } from './_listbox.style.js';
import { listboxModifiers } from './_shared.style.js';
import { getOptionValue } from './option.listbox.js';
import { Option } from './option.toggle-group.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { ToggleButton } from '../../buttons/toggle-button.js';

function InputToggleGroup(props) {
  const internalBaseId = useId();
  const baseId = props.id ?? internalBaseId;
  const fieldsetRef = useRef(null);
  const [isExpanded, actions] = useToggleState();
  const toggleValue = (value, option) => {
    props.toggleValue?.(value, option);
    props.onChange?.((() => {
      if (!props.selectedValues) {
        // if no values were previously selected, then select the toggled
        // value as a starting point
        return [value];
      }
      if (props.selectedValues.includes(value)) {
        // if the toggled value was already selected, then remove it from the
        // user's selection
        return props.selectedValues.filter(it => it !== value);
      }

      // ... otherwise push the new selection to the list of user selections
      return [...props.selectedValues, value];
    })());
  };
  const [uncoloredLayout, setUncoloredLayout] = useState(false);

  // determine whether to use the uncolored layout for listboxes or not when
  // mounting
  useIsomorphicLayoutEffect(() => {
    if (!fieldsetRef.current) {
      return;
    }
    const resolvedColors = resolveComputedCssValues({
      inversedText: colors.text.inversed(),
      accentSurfaceBg: surfaces.accent.default.background()
    }, fieldsetRef.current);
    const canUseInvertedTextOnSecondarySurface = verifyWCAGContrastRatio(resolvedColors.inversedText, resolvedColors.accentSurfaceBg, {
      threshold: "AA-laxed"
    });
    setUncoloredLayout(!canUseInvertedTextOnSecondarySurface);
    fieldsetRef.current.classList.toggle(listboxModifiers.uncolored.className, !canUseInvertedTextOnSecondarySurface);
  }, []);
  const optionsBeforeTruncate = props.maxItemsBeforeTruncate ? props.options.slice(0, props.maxItemsBeforeTruncate) : props.options;
  const optionsAfterTruncate = props.maxItemsBeforeTruncate ? props.options.slice(props.maxItemsBeforeTruncate) : undefined;
  return /*#__PURE__*/jsxs("fieldset", {
    ref: fieldsetRef,
    "data-theme": props.theme,
    className: cx(resets.rootCls, list, uncoloredLayout && listboxModifiers.uncolored.className, props.className),
    "aria-labelledby": props.labelledBy,
    children: [props.legend && /*#__PURE__*/jsx("legend", {
      className: legend,
      children: props.legend
    }), /*#__PURE__*/jsxs("div", {
      children: [optionsBeforeTruncate.map(option => /*#__PURE__*/jsx(Option, {
        selectedValues: props.selectedValues,
        value: option,
        baseId: baseId,
        type: props.type,
        onChange: () => toggleValue(getOptionValue(option), option)
      }, typeof option === "string" ? option : option.id)), !!optionsAfterTruncate?.length && /*#__PURE__*/jsx(TransitionGroup, {
        className: truncateWrapper,
        children: /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          timeout: truncateTransitionDurationMs,
          classNames: transition,
          children: ref => isExpanded ? /*#__PURE__*/jsx("div", {
            ref: ref,
            className: transition,
            children: optionsAfterTruncate.map(option => /*#__PURE__*/jsx(Option, {
              selectedValues: props.selectedValues,
              value: option,
              baseId: baseId,
              type: props.type,
              onChange: () => toggleValue(getOptionValue(option), option)
            }, typeof option === "string" ? option : option.id))
          }) : /*#__PURE__*/jsx(ToggleButton, {
            className: cx(expandButton, transition),
            ref: ref,
            isExpanded: false,
            theme: "compact",
            onClick: actions.toggle,
            children: "Se alle"
          })
        }, String(isExpanded))
      }), props.options.length === 0 && /*#__PURE__*/jsx("div", {
        className: noMatches,
        children: "Vi fandt ikke nogen valgmuligheder."
      })]
    })]
  });
}

export { InputToggleGroup };
//# sourceMappingURL=_input-toggle-group.js.map
